/**
 * Business Settings — the white-label control room.
 *
 * The one-stop hub a developer rebrands the whole platform from: the platform name, the logo
 * that drives everything (console, app, and every email template by default), the two brand
 * colours, the support/contact addresses, the legal links, the socials, and the default
 * currency. It reads the singleton `platform_settings` row and PUTs a patch to
 * `/admin/settings/business`; the logo uploads through the existing brand-asset route and its
 * returned key is stored on the settings row. A live brand preview reflects edits before they
 * are saved. Superadmin only (the route guard and the API both enforce).
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { Link } from 'react-router-dom';

import { messageOf } from '../api/errors.js';
import { useAuth } from '../auth/AuthContext.js';
import { adminCan } from '../contracts/permissions.js';
import type { PlatformSettings, SetPlatformSettingsRequest, SocialLinks } from '../contracts/types.js';
import { CURRENCY_CODES, SOCIAL_NETWORKS } from '../contracts/vocab.js';
import { Button, Card, ErrorBox, Field, Input, LoadingState, Select } from '../components/ui.js';
import {
  IconBell,
  IconChevronDown,
  IconChevronUp,
  IconExternalLink,
  IconFlag,
  IconImage,
  IconMail,
  IconProviders,
  IconSettings,
} from '../components/icons.js';
import { useToast } from '../components/Toast.js';
import { useAsync } from '../hooks/useAsync.js';

const MAX_LOGO_BYTES = 1024 * 1024;
const ALLOWED_LOGO_MIMES = ['image/png', 'image/jpeg', 'image/webp'] as const;

/** One ledger currency. Named off the contract so a change there breaks this, not production. */
type CurrencyCode = PlatformSettings['defaultCurrency'];

/**
 * The contract's floor on the Convert picker: at least two currencies, because a conversion
 * needs something to convert *to*. Mirrored here only to say so before the API does.
 */
const MIN_CONVERSION_CURRENCIES = 2;

const SOCIAL_LABELS: Readonly<Record<(typeof SOCIAL_NETWORKS)[number], string>> = {
  twitter: 'X / Twitter',
  facebook: 'Facebook',
  instagram: 'Instagram',
  linkedin: 'LinkedIn',
  telegram: 'Telegram',
  whatsapp: 'WhatsApp',
  tiktok: 'TikTok',
  youtube: 'YouTube',
};

function toBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Could not read the file.'));
    reader.onload = () => {
      const result = String(reader.result);
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.readAsDataURL(file);
  });
}

export function BusinessSettingsPage(): JSX.Element {
  const { api } = useAuth();
  const state = useAsync(() => api.getBusinessSettings(), []);

  return (
    <>
      <div className="page-head">
        <h1 className="page-head__title">Business settings</h1>
        <p className="page-head__sub">
          Rebrand everything from one place: name, logo, colours, contacts, legal links and socials. It drives the app, this console and every email customers receive.
        </p>
      </div>

      {state.loading ? (
        <LoadingState label="Loading business settings…" />
      ) : state.error !== null ? (
        <ErrorBox message={state.error} />
      ) : state.data === null ? (
        <ErrorBox message="Business settings are not available." />
      ) : (
        <SettingsForm initial={state.data} onSaved={(next) => state.setData(() => next)} />
      )}
    </>
  );
}

function SettingsForm({
  initial,
  onSaved,
}: {
  initial: PlatformSettings;
  onSaved: (next: PlatformSettings) => void;
}): JSX.Element {
  const { api, session } = useAuth();
  const toast = useToast();
  const canWrite = session !== null && adminCan(session.me.role, 'admins:write');

  const [platformName, setPlatformName] = useState(initial.platformName);
  const [primaryColor, setPrimaryColor] = useState(initial.primaryColor);
  const [accentColor, setAccentColor] = useState(initial.accentColor);
  const [supportEmail, setSupportEmail] = useState(initial.supportEmail);
  const [contactEmail, setContactEmail] = useState(initial.contactEmail);
  const [websiteUrl, setWebsiteUrl] = useState(initial.websiteUrl ?? '');
  const [termsUrl, setTermsUrl] = useState(initial.termsUrl ?? '');
  const [privacyUrl, setPrivacyUrl] = useState(initial.privacyUrl ?? '');
  const [defaultCurrency, setDefaultCurrency] = useState(initial.defaultCurrency);
  // Order matters and is the operator's: this array *is* the Convert screen's picker order,
  // so it is held as a list rather than a set.
  const [conversionCurrencies, setConversionCurrencies] = useState<readonly CurrencyCode[]>(
    initial.conversionCurrencies,
  );
  const [social, setSocial] = useState<SocialLinks>(initial.social);
  const [logoAssetKey, setLogoAssetKey] = useState(initial.logoAssetKey);

  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const logoInput = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    return () => {
      if (logoPreview !== null) URL.revokeObjectURL(logoPreview);
    };
  }, [logoPreview]);

  function setSocialField(key: (typeof SOCIAL_NETWORKS)[number], value: string) {
    setSocial((prev) => {
      const next = { ...prev };
      const trimmed = value.trim();
      if (trimmed === '') delete next[key];
      else next[key] = trimmed;
      return next;
    });
  }

  async function onLogo(file: File | undefined) {
    if (file === undefined) return;
    if (!(ALLOWED_LOGO_MIMES as readonly string[]).includes(file.type)) {
      toast.error('Unsupported image', 'Choose a PNG, JPG or WebP logo.');
      return;
    }
    if (file.size > MAX_LOGO_BYTES) {
      toast.error('File too large', 'Keep the logo under 1 MB.');
      return;
    }
    if (logoPreview !== null) URL.revokeObjectURL(logoPreview);
    setLogoPreview(URL.createObjectURL(file));
    setUploadingLogo(true);
    try {
      const dataBase64 = await toBase64(file);
      const asset = await api.uploadBrandLogo({ mime: file.type as (typeof ALLOWED_LOGO_MIMES)[number], dataBase64 });
      setLogoAssetKey(asset.key);
      toast.success('Logo uploaded', 'Save to apply it across the platform.');
    } catch (err) {
      toast.error('Upload failed', messageOf(err));
    } finally {
      setUploadingLogo(false);
    }
  }

  async function save() {
    setFormError(null);
    if (platformName.trim() === '') {
      setFormError('The platform name cannot be empty.');
      return;
    }
    const hex = /^#[0-9A-Fa-f]{6}$/;
    if (!hex.test(primaryColor) || !hex.test(accentColor)) {
      setFormError('Brand colours must be 6-digit hex values, e.g. #5B2CE6.');
      return;
    }
    const emailRe = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
    if (!emailRe.test(supportEmail) || !emailRe.test(contactEmail)) {
      setFormError('Enter valid support and contact email addresses.');
      return;
    }
    if (conversionCurrencies.length < MIN_CONVERSION_CURRENCIES) {
      setFormError(
        `The Convert screen needs at least ${MIN_CONVERSION_CURRENCIES} currencies. A conversion has to have something to convert to.`,
      );
      return;
    }

    const nullableUrl = (v: string): string | null => (v.trim() === '' ? null : v.trim());
    const payload: SetPlatformSettingsRequest = {
      platformName: platformName.trim(),
      logoAssetKey,
      primaryColor: primaryColor.toUpperCase(),
      accentColor: accentColor.toUpperCase(),
      supportEmail: supportEmail.trim(),
      contactEmail: contactEmail.trim(),
      websiteUrl: nullableUrl(websiteUrl),
      termsUrl: nullableUrl(termsUrl),
      privacyUrl: nullableUrl(privacyUrl),
      social,
      defaultCurrency,
      // Sent as an array because the order *is* the picker order, not incidental.
      conversionCurrencies: [...conversionCurrencies],
    };

    setSaving(true);
    try {
      const next = await api.setBusinessSettings(payload);
      onSaved(next);
      toast.success('Business settings saved', 'Your branding is live across the platform.');
    } catch (err) {
      toast.error('Could not save', messageOf(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="settings-hub">
      <div className="grid grid--2">
        <Card title="Identity">
          <Field label="Platform name" htmlFor="pf-name">
            <Input
              id="pf-name"
              value={platformName}
              maxLength={80}
              disabled={!canWrite}
              placeholder="Your platform name"
              onChange={(e) => setPlatformName(e.target.value)}
            />
          </Field>

          <div className="field">
            <span className="field__label">Logo (drives the app, console and emails)</span>
            <div
              className="dropzone dropzone--sm"
              role="button"
              tabIndex={0}
              onClick={() => canWrite && logoInput.current?.click()}
              onKeyDown={(e) => {
                if ((e.key === 'Enter' || e.key === ' ') && canWrite) logoInput.current?.click();
              }}
            >
              {logoPreview !== null ? (
                <img className="logo-chip" src={logoPreview} alt="Logo preview" />
              ) : logoAssetKey !== null ? (
                <div className="logo-chip logo-chip--stored">
                  <IconImage size={20} />
                  <span className="mono" style={{ fontSize: 11 }}>{logoAssetKey}</span>
                </div>
              ) : (
                <div style={{ display: 'grid', placeItems: 'center', gap: 6 }}>
                  <IconImage size={24} />
                  <span className="faint" style={{ fontSize: 12 }}>Click to upload · PNG, JPG, WebP · ≤ 1 MB</span>
                </div>
              )}
              <input
                ref={logoInput}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                hidden
                disabled={!canWrite}
                onChange={(e) => void onLogo(e.target.files?.[0])}
              />
            </div>
            {uploadingLogo && <div className="faint" style={{ fontSize: 12, marginTop: 6 }}>Uploading…</div>}
          </div>

          <div className="grid grid--2" style={{ gap: 14 }}>
            <ColorField label="Primary colour" value={primaryColor} onChange={setPrimaryColor} disabled={!canWrite} />
            <ColorField label="Accent colour" value={accentColor} onChange={setAccentColor} disabled={!canWrite} />
          </div>

          <Field label="Default currency" htmlFor="pf-cur">
            <Select
              id="pf-cur"
              value={defaultCurrency}
              disabled={!canWrite}
              onChange={(e) => setDefaultCurrency(e.target.value as PlatformSettings['defaultCurrency'])}
            >
              {CURRENCY_CODES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </Select>
          </Field>
        </Card>

        <BrandPreview name={platformName} primary={primaryColor} accent={accentColor} logo={logoPreview} />
      </div>

      <div className="grid grid--2">
        <Card title="Contact & legal">
          <Field label="Support email" htmlFor="pf-support">
            <Input id="pf-support" type="email" value={supportEmail} disabled={!canWrite} onChange={(e) => setSupportEmail(e.target.value)} />
          </Field>
          <Field label="Contact email" htmlFor="pf-contact">
            <Input id="pf-contact" type="email" value={contactEmail} disabled={!canWrite} onChange={(e) => setContactEmail(e.target.value)} />
          </Field>
          <Field label="Website URL" htmlFor="pf-web">
            <Input id="pf-web" type="url" value={websiteUrl} placeholder="https://…" disabled={!canWrite} onChange={(e) => setWebsiteUrl(e.target.value)} />
          </Field>
          <div className="grid grid--2" style={{ gap: 14 }}>
            <Field label="Terms URL" htmlFor="pf-terms">
              <Input id="pf-terms" type="url" value={termsUrl} placeholder="https://…/terms" disabled={!canWrite} onChange={(e) => setTermsUrl(e.target.value)} />
            </Field>
            <Field label="Privacy URL" htmlFor="pf-privacy">
              <Input id="pf-privacy" type="url" value={privacyUrl} placeholder="https://…/privacy" disabled={!canWrite} onChange={(e) => setPrivacyUrl(e.target.value)} />
            </Field>
          </div>
        </Card>

        <Card title="Social links">
          <p className="muted" style={{ marginTop: 0, fontSize: 13 }}>
            Wire only the networks you use. Each appears in the app and site footer. Leave the rest blank.
          </p>
          <div className="social-grid">
            {SOCIAL_NETWORKS.map((key) => (
              <Field key={key} label={SOCIAL_LABELS[key]} htmlFor={`soc-${key}`}>
                <Input
                  id={`soc-${key}`}
                  type="url"
                  value={social[key] ?? ''}
                  placeholder="https://…"
                  disabled={!canWrite}
                  onChange={(e) => setSocialField(key, e.target.value)}
                />
              </Field>
            ))}
          </div>
        </Card>
      </div>

      <ConversionCurrencies
        selected={conversionCurrencies}
        disabled={!canWrite}
        onChange={setConversionCurrencies}
      />

      <QuickLinks />

      <div className="settings-actions">
        {formError !== null && <div className="form-error">{formError}</div>}
        {!canWrite && <div className="muted" style={{ fontSize: 13 }}>Your role can view but not change these settings.</div>}
        <Button variant="primary" loading={saving} disabled={!canWrite} onClick={() => void save()}>
          Save business settings
        </Button>
      </div>
    </div>
  );
}

/**
 * Which currencies the app's Convert screen offers, and in what order.
 *
 * A list rather than a set, and reordered with the same checkbox-plus-arrows control the
 * failover priority list uses, because the order is the picker order a customer sees and the
 * first entry is what the screen opens on. Selected currencies float to the top in their own
 * order; the rest sit below, waiting.
 *
 * The API refuses fewer than two, so the last two checkboxes lock rather than letting an
 * operator save a Convert screen with nothing to convert to.
 */
function ConversionCurrencies({
  selected,
  disabled,
  onChange,
}: {
  selected: readonly CurrencyCode[];
  disabled: boolean;
  onChange: (next: readonly CurrencyCode[]) => void;
}): JSX.Element {
  // Selected first, in the operator's order; then everything else, in the contract's.
  const rest = CURRENCY_CODES.filter((c) => !selected.includes(c));
  const rows: readonly CurrencyCode[] = [...selected, ...rest];
  const atFloor = selected.length <= MIN_CONVERSION_CURRENCIES;

  function toggle(code: CurrencyCode, on: boolean): void {
    if (on) {
      onChange([...selected, code]);
      return;
    }
    // Removing the second-to-last would leave a Convert screen the API will not accept, so
    // the control refuses rather than letting the save fail later.
    if (atFloor) return;
    onChange(selected.filter((c) => c !== code));
  }

  function move(code: CurrencyCode, direction: -1 | 1): void {
    const index = selected.indexOf(code);
    const target = index + direction;
    if (index === -1 || target < 0 || target >= selected.length) return;
    const next = [...selected];
    const [item] = next.splice(index, 1);
    next.splice(target, 0, item!);
    onChange(next);
  }

  return (
    <Card
      title="Currencies the Convert screen offers"
      actions={<span className="faint" style={{ fontSize: 12 }}>{selected.length} selected</span>}
    >
      <p className="muted" style={{ marginTop: -4 }}>
        The app&rsquo;s Convert screen offers exactly this list, in this order, and opens on the
        first entry. The API refuses a quote for anything outside it, so the client is told rather
        than trusted. It lives here and not in a release because a desk that has stopped making a
        market in a coin needs it gone from the picker that afternoon.
      </p>

      <p className="muted">
        Tick a currency to offer it, and use the arrows to set the order the picker lists them in.
      </p>

      <div className="prov-list" style={{ marginTop: 14 }}>
        {rows.map((code) => {
          const index = selected.indexOf(code);
          const on = index !== -1;
          return (
            <div key={code} className={`prov-item${on ? '' : ' prov-item--disabled'}`}>
              <span className="prov-item__rank">{on ? index + 1 : '–'}</span>
              <div className="prov-move">
                <button
                  type="button"
                  aria-label={`Move ${code} up`}
                  disabled={disabled || !on || index <= 0}
                  onClick={() => move(code, -1)}
                >
                  <IconChevronUp size={11} />
                </button>
                <button
                  type="button"
                  aria-label={`Move ${code} down`}
                  disabled={disabled || !on || index >= selected.length - 1}
                  onClick={() => move(code, 1)}
                >
                  <IconChevronDown size={11} />
                </button>
              </div>
              <span className="prov-item__name">{code}</span>
              {on && index === 0 && <span className="badge badge--violet">Opens here</span>}
              <span className="prov-item__spacer" />
              <label className="conv-check">
                <input
                  type="checkbox"
                  checked={on}
                  disabled={disabled || (on && atFloor)}
                  onChange={(e) => toggle(code, e.target.checked)}
                />
                <span className="faint" style={{ fontSize: 12 }}>
                  {on ? (atFloor ? 'Offered, at the minimum of two' : 'Offered') : 'Not offered'}
                </span>
              </label>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

function ColorField({
  label,
  value,
  onChange,
  disabled,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  disabled: boolean;
}): JSX.Element {
  return (
    <div className="field">
      <span className="field__label">{label}</span>
      <div className="color-field">
        <input
          type="color"
          className="color-field__swatch"
          value={/^#[0-9A-Fa-f]{6}$/.test(value) ? value : '#000000'}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value.toUpperCase())}
          aria-label={`${label} swatch`}
        />
        <Input
          value={value}
          maxLength={7}
          disabled={disabled}
          className="mono"
          onChange={(e) => onChange(e.target.value.toUpperCase())}
        />
      </div>
    </div>
  );
}

function BrandPreview({
  name,
  primary,
  accent,
  logo,
}: {
  name: string;
  primary: string;
  accent: string;
  logo: string | null;
}): JSX.Element {
  const safePrimary = /^#[0-9A-Fa-f]{6}$/.test(primary) ? primary : '#5B2CE6';
  const safeAccent = /^#[0-9A-Fa-f]{6}$/.test(accent) ? accent : '#16C784';
  return (
    <Card title="Live brand preview">
      <div className="brand-preview" style={{ '--pp': safePrimary, '--pa': safeAccent } as CSSProperties}>
        <div className="brand-preview__bar">
          {logo !== null ? (
            <img src={logo} alt="" className="brand-preview__logo" />
          ) : (
            <span className="brand-preview__word">{name || 'Your brand'}</span>
          )}
        </div>
        <div className="brand-preview__body">
          <div className="brand-preview__balance">
            <span className="faint" style={{ fontSize: 12 }}>Wallet balance</span>
            <strong style={{ fontSize: 24 }}>₦248,300.00</strong>
          </div>
          <div className="brand-preview__actions">
            <span className="brand-preview__btn brand-preview__btn--primary">Send</span>
            <span className="brand-preview__btn brand-preview__btn--accent">Settled</span>
          </div>
          <div className="brand-preview__swatches">
            <span className="brand-preview__dot" style={{ background: safePrimary }} />
            <span className="mono faint" style={{ fontSize: 12 }}>{safePrimary}</span>
            <span className="brand-preview__dot" style={{ background: safeAccent }} />
            <span className="mono faint" style={{ fontSize: 12 }}>{safeAccent}</span>
          </div>
        </div>
      </div>
    </Card>
  );
}

function QuickLinks(): JSX.Element {
  const links = useMemo(
    () => [
      { to: '/email-templates', label: 'Email templates', hint: 'Edit every transactional email', Icon: IconMail },
      { to: '/push', label: 'Push broadcast', hint: 'Message the whole customer base', Icon: IconBell },
      { to: '/flags', label: 'Feature flags', hint: 'Turn features on and off', Icon: IconFlag },
      { to: '/providers', label: 'Providers & fees', hint: 'Payment routing and failover', Icon: IconProviders },
      { to: '/brand', label: 'Brand assets', hint: 'The operator logo store', Icon: IconImage },
      { to: '/support-settings', label: 'Support settings', hint: 'Hours, auto-reply, escalation', Icon: IconSettings },
    ],
    [],
  );
  return (
    <Card title="Configure the rest of the platform">
      <div className="quicklinks">
        {links.map(({ to, label, hint, Icon }) => (
          <Link key={to} to={to} className="quicklink">
            <span className="quicklink__icon">
              <Icon size={18} />
            </span>
            <span className="quicklink__text">
              <span className="quicklink__label">{label}</span>
              <span className="quicklink__hint">{hint}</span>
            </span>
            <IconExternalLink size={15} />
          </Link>
        ))}
      </div>
    </Card>
  );
}
